import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "item",
    "handleLeft",
    "handleRight",
    "gridBackground",
    "itemBars",
    "ganttBody",
  ];
  static values = {
    totalDays: Number,
    chartStartDate: String,
  };

  connect() {
    this.resetProperties();

    this.columnWidth = 0;
    this.rowHeight = 0;

    this.boundDragMove = this.dragMove.bind(this);
    this.boundDragEnd = this.dragEnd.bind(this);

    this.calculateGridMetrics();
    this.calculateRowHeight();

    this.currentOrder = [...this.itemTargets];

    this.scrollToCurrentDate(new Event("fake"), true);
  }

  disconnect() {
    if (this.dragging) {
      this.dragEnd();
    }
  }

  resetProperties() {
    this.dragging = false;
    this.dragMode = null;
    this.potentialDragType = null;
    this.draggedItem = null;
    this.initialMouseX = 0;
    this.initialMouseY = 0;
    this.initialGridStart = 0;
    this.initialGridSpan = 0;
    this.initialItemTop = 0;
  }

  scrollToCurrentDate(event, instant = false) {
    event.preventDefault();
    event.stopPropagation();
    const currentDate = new Date();
    const chartStartDate = new Date(this.chartStartDateValue);

    // Add padding of 3 days to the start date
    chartStartDate.setDate(chartStartDate.getDate() + 3);

    const daysFromStart = Math.floor(
      (currentDate - chartStartDate) / (1000 * 60 * 60 * 24)
    );

    const scrollPosition = daysFromStart * this.columnWidth;
    this.ganttBodyTarget.scrollTo({
      left: scrollPosition,
      behavior: instant ? "auto" : "smooth",
    });
  }

  calculateGridMetrics() {
    if (
      !this.hasGridBackgroundTarget ||
      !this.totalDaysValue ||
      this.totalDaysValue <= 0
    ) {
      console.error(
        "Gantt Drag: Cannot calculate grid metrics. Missing target or totalDays value."
      );
      this.columnWidth = 0;
      return;
    }
    const gridWidth = this.gridBackgroundTarget.scrollWidth;
    this.columnWidth = gridWidth / this.totalDaysValue;

    if (this.columnWidth <= 0) {
      console.error(
        "Gantt Drag: Calculated column width is invalid.",
        gridWidth,
        this.totalDaysValue
      );
    }
  }

  calculateRowHeight() {
    if (this.hasItemTargets && this.itemTargets.length > 0) {
      const firstItem = this.itemTargets[0];
      if (firstItem.offsetHeight > 0) {
        const style = window.getComputedStyle(firstItem);
        const marginTop = parseFloat(style.marginTop);
        const marginBottom = parseFloat(style.marginBottom);
        this.rowHeight = firstItem.offsetHeight + marginTop + marginBottom;
      } else {
        console.warn(
          "Gantt Drag: Could not measure row height from first item target."
        );
        const defaultRowHeight = parseInt(
          window.getComputedStyle(this.itemBarsTarget).gridAutoRows,
          10
        );
        this.rowHeight = defaultRowHeight > 0 ? defaultRowHeight : 50;
      }
    } else {
      console.warn(
        "Gantt Drag: No item targets available to measure row height."
      );
      this.rowHeight = 50;
    }
  }

  updateGridRows() {
    this.currentOrder.forEach((item, index) => {
      if (this.draggedItem !== item) {
        item.style.position = "";
        item.style.top = "";
        item.style.left = "";
        item.style.width = "";
        item.style.height = "";
        item.style.zIndex = "";
      }
      item.style.gridRow = index + 1;
    });
  }

  getItemFromEvent(event) {
    return event.target.closest('[data-gantt-drag-target="item"]');
  }

  dragStart(event) {
    const item = this.getItemFromEvent(event);
    if (!item) return;

    delete item.dataset.justDragged;

    this.calculateGridMetrics();
    this.calculateRowHeight();

    if (this.columnWidth <= 0 || this.rowHeight <= 0) {
      console.error("Gantt Drag: Aborting drag start due to invalid metrics.");
      return;
    }

    this.dragging = true;
    this.draggedItem = item;
    this.initialMouseX = event.pageX;
    this.initialMouseY = event.pageY;
    this.dragMode = "none";

    const itemRect = item.getBoundingClientRect();
    this.initialItemTop = itemRect.top;

    const gridColumnStyle = window
      .getComputedStyle(item)
      .gridColumn.split(" / ");
    this.initialGridStart = parseInt(gridColumnStyle[0], 10);

    if (gridColumnStyle.length > 1 && gridColumnStyle[1].includes("span")) {
      this.initialGridSpan = parseInt(
        gridColumnStyle[1].replace("span ", ""),
        10
      );
    } else if (gridColumnStyle.length > 1) {
      const endCol = parseInt(gridColumnStyle[1], 10);
      this.initialGridSpan = endCol - this.initialGridStart;
    } else {
      this.initialGridSpan = 1;
    }

    if (isNaN(this.initialGridStart) || isNaN(this.initialGridSpan)) {
      console.error(
        "Gantt Drag: Could not parse initial grid start/span",
        window.getComputedStyle(item).gridColumn
      );
      this.dragging = false;
      return;
    }

    this.draggedItem.dataset.originalGridStart = this.initialGridStart;
    this.draggedItem.dataset.originalGridSpan = this.initialGridSpan;
    this.draggedItem.dataset.originalItemIndex =
      this.currentOrder.indexOf(item);

    if (event.target.dataset.ganttDragTarget === "handleLeft") {
      this.potentialDragType = "horizontal-resize-left";
      this.draggedItem.style.cursor = "ew-resize";
    } else if (event.target.dataset.ganttDragTarget === "handleRight") {
      this.potentialDragType = "horizontal-resize-right";
      this.draggedItem.style.cursor = "ew-resize";
    } else {
      this.potentialDragType = "move";
      this.draggedItem.style.cursor = "grab";
    }

    event.preventDefault();
    document.body.style.userSelect = "none";
    this.draggedItem.classList.add("is-dragging");

    document.addEventListener("mousemove", this.boundDragMove);
    document.addEventListener("mouseup", this.boundDragEnd, { once: true });
  }

  dragMove(event) {
    if (
      !this.dragging ||
      !this.draggedItem ||
      this.columnWidth <= 0 ||
      this.rowHeight <= 0
    )
      return;

    const currentMouseX = event.pageX;
    const currentMouseY = event.pageY;
    const deltaX = currentMouseX - this.initialMouseX;
    const deltaY = currentMouseY - this.initialMouseY;

    if (this.dragMode === "none") {
      const moveThreshold = 5;
      if (
        Math.abs(deltaX) > moveThreshold ||
        Math.abs(deltaY) > moveThreshold
      ) {
        if (
          Math.abs(deltaX) > Math.abs(deltaY) ||
          this.potentialDragType.startsWith("horizontal-resize")
        ) {
          this.dragMode =
            this.potentialDragType === "move"
              ? "horizontal-move"
              : this.potentialDragType;

          const currentGridStyle = window
            .getComputedStyle(this.draggedItem)
            .gridColumn.split(" / ");
          this.initialGridStart = parseInt(currentGridStyle[0], 10);
          if (
            currentGridStyle.length > 1 &&
            currentGridStyle[1].includes("span")
          ) {
            this.initialGridSpan = parseInt(
              currentGridStyle[1].replace("span ", ""),
              10
            );
          } else if (currentGridStyle.length > 1) {
            const endCol = parseInt(currentGridStyle[1], 10);
            this.initialGridSpan = endCol - this.initialGridStart;
          } else {
            this.initialGridSpan = 1;
          }
          this.draggedItem.dataset.originalGridStart = this.initialGridStart;
          this.draggedItem.dataset.originalGridSpan = this.initialGridSpan;
          this.initialMouseX = currentMouseX;
        } else if (this.potentialDragType === "move") {
          this.dragMode = "vertical";
          const itemRect = this.draggedItem.getBoundingClientRect();
          this.draggedItem.classList.add("is-dragging-vertical");
          this.initialItemTop = itemRect.top;
          this.initialMouseY = currentMouseY;
        } else {
          return;
        }
      } else {
        return;
      }
    }

    if (this.dragMode.startsWith("horizontal")) {
      if (this.draggedItem.classList.contains("completed")) {
        return;
      }
      event.preventDefault();

      const deltaColumns = Math.round(deltaX / this.columnWidth);

      let newStart = parseInt(this.draggedItem.dataset.originalGridStart, 10);
      let newSpan = parseInt(this.draggedItem.dataset.originalGridSpan, 10);
      const originalEnd = newStart + newSpan;

      if (this.dragMode === "horizontal-move") {
        newStart = newStart + deltaColumns;
        newStart = Math.max(1, newStart);
        newStart = Math.min(newStart, this.totalDaysValue - newSpan + 1);
      } else if (this.dragMode === "horizontal-resize-left") {
        newStart = newStart + deltaColumns;
        newStart = Math.min(newStart, originalEnd - 1);
        newStart = Math.max(1, newStart);

        newSpan = originalEnd - newStart;
        if (newSpan < 1) {
          newSpan = 1;
          newStart = originalEnd - 1;
        }
      } else if (this.dragMode === "horizontal-resize-right") {
        let newEnd = originalEnd + deltaColumns;
        newStart = parseInt(this.draggedItem.dataset.originalGridStart, 10);

        newEnd = Math.max(newEnd, newStart + 1);
        newEnd = Math.min(newEnd, this.totalDaysValue + 1);

        newSpan = newEnd - newStart;
        if (newSpan < 1) newSpan = 1;
      }

      const currentStart = parseInt(this.draggedItem.style.gridColumnStart, 10);
      const currentSpan = this.getCurrentSpan(this.draggedItem);

      if (newStart !== currentStart || newSpan !== currentSpan) {
        this.draggedItem.style.gridColumnStart = newStart;
        this.draggedItem.style.gridColumnEnd = `span ${newSpan}`;
      }
    } else if (this.dragMode === "vertical") {
      event.preventDefault();

      const itemBarsRect = this.itemBarsTarget.getBoundingClientRect();
      const mouseYRelativeToContainerScroll =
        event.clientY - itemBarsRect.top + this.ganttBodyTarget.scrollTop;
      const draggedItemCenterY_scroll =
        mouseYRelativeToContainerScroll + this.draggedItem.offsetHeight / 2;

      let potentialIndex = Math.floor(
        draggedItemCenterY_scroll / this.rowHeight
      );
      potentialIndex = Math.max(
        0,
        Math.min(potentialIndex, this.currentOrder.length - 1)
      );

      const currentIndex = this.currentOrder.indexOf(this.draggedItem);

      if (potentialIndex !== currentIndex) {
        const [movedItem] = this.currentOrder.splice(currentIndex, 1);
        this.currentOrder.splice(potentialIndex, 0, movedItem);

        this.updateGridRows();
      }
    }
  }

  getCurrentSpan(element) {
    const style = window.getComputedStyle(element).gridColumnEnd;
    if (style && style.startsWith("span")) {
      return parseInt(style.replace("span ", ""), 10);
    }
    const start = parseInt(
      window.getComputedStyle(element).gridColumnStart,
      10
    );
    const end = parseInt(style, 10);
    if (!isNaN(start) && !isNaN(end)) return end - start;

    console.warn(
      "Could not reliably determine span from grid-column-end style:",
      style,
      element
    );
    if (this.columnWidth > 0) {
      const elementWidth = element.offsetWidth;
      const approxSpan = Math.round(elementWidth / this.columnWidth);
      console.warn("Approximating span based on width:", approxSpan);
      return Math.max(1, approxSpan);
    }

    return 1;
  }

  dragEnd(event) {
    if (!this.dragging) {
      this.dragging = false;
      this.dragMode = null;
      this.potentialDragType = null;
      this.draggedItem = null;
      document.removeEventListener("mousemove", this.boundDragMove);
      document.body.style.userSelect = "";
      return;
    }

    const itemThatWasDragged = this.draggedItem;

    document.removeEventListener("mousemove", this.boundDragMove);

    document.body.style.userSelect = "";

    if (itemThatWasDragged) {
      itemThatWasDragged.style.cursor = "grab";
      itemThatWasDragged.classList.remove("is-dragging");
      itemThatWasDragged.classList.remove("is-dragging-vertical");

      let wasActuallyDragged = false;
      if (
        event &&
        typeof event.pageX === "number" &&
        typeof event.pageY === "number"
      ) {
        const deltaX = event.pageX - this.initialMouseX;
        const deltaY = event.pageY - this.initialMouseY;
        const dragThreshold = 5;
        const movedSignificantly =
          Math.abs(deltaX) > dragThreshold || Math.abs(deltaY) > dragThreshold;
        wasActuallyDragged =
          (this.dragMode && this.dragMode !== "none") || movedSignificantly;
      } else {
        wasActuallyDragged = this.dragMode && this.dragMode !== "none";
      }

      if (wasActuallyDragged) {
        itemThatWasDragged.dataset.justDragged = "true";
        setTimeout(() => {
          if (itemThatWasDragged && itemThatWasDragged.dataset.justDragged) {
            delete itemThatWasDragged.dataset.justDragged;
          }
        }, 0);
      }

      if (this.dragMode && this.dragMode.startsWith("horizontal")) {
        this.updateDateAttributes(itemThatWasDragged);

        delete itemThatWasDragged.dataset.originalGridStart;
        delete itemThatWasDragged.dataset.originalGridSpan;
      } else if (this.dragMode === "vertical") {
        const currentIndex = this.currentOrder.indexOf(itemThatWasDragged);
        const hiddenField = itemThatWasDragged.querySelector(
          'input[name="timeline_position"]'
        );
        if (
          hiddenField &&
          parseInt(hiddenField.value, 10) !== currentIndex + 1
        ) {
          hiddenField.value = currentIndex + 1;
          const form = itemThatWasDragged.querySelector("form");
          if (form) {
            form.requestSubmit();
          }
        }
        this.updateGridRows();

        delete itemThatWasDragged.dataset.originalItemIndex;
      } else {
        delete itemThatWasDragged.dataset.originalGridStart;
        delete itemThatWasDragged.dataset.originalGridSpan;
        delete itemThatWasDragged.dataset.originalItemIndex;
      }
    }

    this.resetProperties();

    this.currentOrder = [...this.itemTargets].sort((a, b) => {
      const rowA = parseInt(a.style.gridRow, 10) || 0;
      const rowB = parseInt(b.style.gridRow, 10) || 0;
      return rowA - rowB;
    });
  }

  updateDateAttributes(item) {
    const startCol = parseInt(item.style.gridColumnStart, 10);
    const span = this.getCurrentSpan(item);

    if (isNaN(startCol) || isNaN(span) || span < 1) {
      console.error(
        "Cannot update dates: Invalid grid column/span values.",
        item.style.gridColumnStart,
        span
      );
      return;
    }

    const endCol = startCol + span - 1;

    let startDateCalc = new Date(this.chartStartDateValue);
    startDateCalc.setDate(startDateCalc.getDate() + startCol - 1);
    const formattedStartDate = startDateCalc.toISOString().split("T")[0];

    let endDateCalc = new Date(this.chartStartDateValue);
    endDateCalc.setDate(endDateCalc.getDate() + endCol - 1);
    const formattedEndDate = endDateCalc.toISOString().split("T")[0];

    // Update tooltip dates
    const tooltipElement = item.querySelector(
      ".stimulus-tooltip .gantt-tooltip-dates"
    );
    if (tooltipElement) {
      const newTooltipText = `${this.formatDateForTooltip(
        startDateCalc
      )} - ${this.formatDateForTooltip(endDateCalc)}`;
      tooltipElement.textContent = newTooltipText;
    }

    const form = item.querySelector("form");
    const startDateInput = form.querySelector('input[name="start_date"]');
    const endDateInput = form.querySelector('input[name="end_date"]');

    let formNeedsSubmit = false;

    if (formattedStartDate !== startDateInput.value) {
      startDateInput.value = formattedStartDate;
      formNeedsSubmit = true;
    }

    if (formattedEndDate !== endDateInput.value) {
      endDateInput.value = formattedEndDate;
      formNeedsSubmit = true;
    }

    if (formNeedsSubmit) {
      form.requestSubmit();
    }
  }

  formatDateForTooltip(date) {
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "short" });
    const year = date.getFullYear();
    let suffix = "th";
    if (day === 1 || day === 21 || day === 31) suffix = "st";
    else if (day === 2 || day === 22) suffix = "nd";
    else if (day === 3 || day === 23) suffix = "rd";
    return `${day}${suffix} ${month} ${year}`;
  }
}
